import React, { useCallback, useMemo } from "react";
import { createEditor, Descendant, BaseEditor, Editor, Text } from "slate";
import {
  Slate,
  Editable,
  withReact,
  RenderElementProps,
  RenderLeafProps,
  ReactEditor,
  useSlate,
} from "slate-react";
import { withHistory, HistoryEditor } from "slate-history";
import { Bold, Italic, Check, X } from "lucide-react";
import { Button } from "@core/index";

interface BaseElement {
  type: ElementType;
  align?: Alignment;
  children: CustomText[];
}

interface ParagraphElement extends BaseElement {
  type: "paragraph";
}

interface HeadingElement extends BaseElement {
  type:
    | "heading-one"
    | "heading-two"
    | "heading-three"
    | "heading-four"
    | "heading-five"
    | "heading-six";
}

interface BulletedListElement extends BaseElement {
  type: "bulleted-list";
}

interface NumberedListElement extends BaseElement {
  type: "numbered-list";
}

interface ListItemElement extends BaseElement {
  type: "list-item";
}

interface AIContentElement extends BaseElement {
  type: "ai-content";
}

type CustomElement =
  | ParagraphElement
  | HeadingElement
  | BulletedListElement
  | NumberedListElement
  | ListItemElement
  | AIContentElement;

type ElementType =
  | "paragraph"
  | "heading-one"
  | "heading-two"
  | "heading-three"
  | "heading-four"
  | "heading-five"
  | "heading-six"
  | "bulleted-list"
  | "numbered-list"
  | "list-item"
  | "ai-content";

type TextSize = "sm" | "md" | "lg" | "xl";
type Alignment = "left" | "center" | "right" | "justify";

type CustomText = {
  text: string;
  size?: TextSize;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: Alignment;
};

// Extend the Slate types
declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

const initialValue: Descendant[] = [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
];

// Deserialize text with markdown to Slate format
const deserialize = (text: string): Descendant[] => {
  const nodes: Descendant[] = [];
  let currentParagraph: CustomElement = { type: "paragraph", children: [] };

  // Simplified regex to match bold and italic patterns
  const regex = /(\*\*[\s\S]+?\*\*|\*[\s\S]+?\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add any text before the match
    if (match.index > lastIndex) {
      currentParagraph.children.push({
        text: text.slice(lastIndex, match.index),
      });
    }

    const segment = match[0].trim();
    if (segment.startsWith("**") && segment.endsWith("**")) {
      currentParagraph.children.push({
        text: segment.slice(2, -2).trim(),
        bold: true,
      });
    } else if (segment.startsWith("*") && segment.endsWith("*")) {
      currentParagraph.children.push({
        text: segment.slice(1, -1).trim(),
        italic: true,
      });
    }

    lastIndex = regex.lastIndex;
  }

  // Add any remaining text
  if (lastIndex < text.length) {
    currentParagraph.children.push({
      text: text.slice(lastIndex),
    });
  }

  if (currentParagraph.children.length > 0) {
    nodes.push(currentParagraph);
  }

  return nodes.length > 0 ? nodes : initialValue;
};

// Serialize Slate format back to markdown
const serialize = (nodes: Descendant[]): string => {
  return nodes
    .map((node) => {
      if (!("children" in node)) return "";

      return node.children
        .map((child) => {
          if (!Text.isText(child)) return "";
          let text = child.text.trim();

          if (!text) return "";

          if (child.bold) {
            text = `**${text}**`;
          }
          if (child.italic) {
            text = `*${text}*`;
          }

          return text;
        })
        .join(" ")
        .trim();
    })
    .join("\n")
    .trim();
};

const toggleMark = (
  editor: BaseEditor & ReactEditor & HistoryEditor,
  format: keyof Omit<CustomText, "text">,
) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isMarkActive = (
  editor: BaseEditor & ReactEditor & HistoryEditor,
  format: keyof Omit<CustomText, "text">,
) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

interface ToolbarButtonProps {
  format: keyof Omit<CustomText, "text">;
  icon: React.ReactNode;
}

const ToolbarButton = ({ format, icon }: ToolbarButtonProps) => {
  const editor = useSlate();
  return (
    <button
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      className={`p-1 mx-1 text-sm rounded ${
        isMarkActive(editor, format)
          ? "bg-gray-200 text-gray-900"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      {icon}
    </button>
  );
};

interface ToolbarProps {
  onSave?: () => void;
  onCancel?: () => void;
}

const Toolbar = ({ onSave, onCancel }: ToolbarProps) => {
  return (
    <div className="flex items-center justify-between px-1 py-0.5 border-b border-gray-200">
      <div>
        <ToolbarButton format="bold" icon={<Bold size={14} />} />
        <ToolbarButton format="italic" icon={<Italic size={14} />} />
      </div>
      <div className="flex items-center space-x-1">
        {onCancel && (
          <Button
            onClick={onCancel}
            size="xs"
            aria-label="Cancel edit"
            bgColor="bg-base-200"
            textColor="text-base-content"
            rounded="full"
          >
            <X size={12} />
          </Button>
        )}
        {onSave && (
          <Button
            onClick={onSave}
            size="xs"
            aria-label="Save changes"
            bgColor="bg-primary-400"
            textColor="text-white"
            rounded="full"
          >
            <Check size={12} />
          </Button>
        )}
      </div>
    </div>
  );
};

const RichTextEditor = ({
  value,
  onChange,
  onSave,
  onCancel,
  isSaving,
}: {
  value: string;
  onChange: (value: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
}) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} />,
    [],
  );
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    [],
  );

  const initialEditorValue = useMemo(() => {
    if (typeof value === "string") {
      return deserialize(value);
    }
    // Ensure value is always an array of Descendant
    return Array.isArray(value) ? value : initialValue;
  }, [value]);

  return (
    <div className="relative border rounded-md">
      {isSaving && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-50 rounded-md">
          <span className="loading loading-spinner loading-md text-primary-400"></span>
        </div>
      )}
      <Slate
        editor={editor}
        initialValue={initialEditorValue}
        onChange={(newValue) => {
          // Check if the editor is actually changing before calling onChange
          const isAstChange = editor.operations.some(
            (op) => op.type !== "set_selection",
          );
          if (isAstChange) {
            const markdownValue = serialize(newValue);
            onChange(markdownValue);
          }
        }}
      >
        <Toolbar onSave={onSave} onCancel={onCancel} />
        <div className="p-2 min-h-[6rem]">
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder="Enter description..."
            spellCheck
            autoFocus
            style={{ outline: "none" }}
          />
        </div>
      </Slate>
    </div>
  );
};

const Element = ({ attributes, children }: RenderElementProps) => {
  return <p {...attributes}>{children}</p>;
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.bold) {
    children = <strong> {children} </strong>;
  }

  if (leaf.italic) {
    children = <em> {children} </em>;
  }

  return <span {...attributes}>{children}</span>;
};

export default RichTextEditor;
